import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { GoogleMap, LoadScript, Marker, DirectionsRenderer } from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "./config.js";
import { Clock, Navigation2, MapPin, Phone, User, DollarSign, AlertCircle } from 'lucide-react';
import { useParams, useSearchParams } from "react-router-dom";
import { API_URL } from './config';

const Director = () => {
    const { tripId } = useParams();
    const [searchParams] = useSearchParams();
    const [directions, setDirections] = useState(null);
    const [driverLocation, setDriverLocation] = useState(null);
    const [tripData, setTripData] = useState([]);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [tripInfo, setTripInfo] = useState(null);
    const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(true);

    // Check if we have query parameters (from mobile app) or tripId (from web)
    const startLat = searchParams.get('startLat');
    const startLng = searchParams.get('startLng');
    const endLat = searchParams.get('endLat');
    const endLng = searchParams.get('endLng');
    const driverLat = searchParams.get('driverLat');
    const driverLng = searchParams.get('driverLng');
    const useQueryParams = startLat && startLng && endLat && endLng;

    // Set initial driver location from query parameters if available
    useEffect(() => {
        if (driverLat && driverLng && !driverLocation) {
            console.log("🚗 Using driver location from mobile app query parameters:", {
                lat: driverLat,
                lng: driverLng
            });
            setDriverLocation({
                latitude: parseFloat(driverLat),
                longitude: parseFloat(driverLng),
            });
        }
    }, [driverLat, driverLng, driverLocation]);

    const center = {
        lat: useQueryParams ? parseFloat(startLat) : (tripData.length ? tripData[0].origin_location_lat : 37.7749),
        lng: useQueryParams ? parseFloat(startLng) : (tripData.length ? tripData[0].origin_location_long : -122.4194),
    };

    const handleEndTrip = () => {
        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage("endTrip"); // Send message to WebView
        }
    };

    const speakDirection = (text) => {
        const utterance = new SpeechSynthesisUtterance(text);
        speechSynthesis.speak(utterance);
    };

    const fetchTripDetails = async () => {
        try {
            const response = await fetch(`${API_URL}/trip/${tripId}`);
            if (!response.ok) throw new Error("Network response was not ok");
            const data = await response.json();
            setTripData(data);

            const greeting = `Hello! Your trip starts from ${data[0].origin_location} to ${data[0].dest_location}. Safe travels!`;
            speakDirection(greeting);
        } catch (error) {
            console.error("Error fetching trip details:", error);
        }
    };

    useEffect(() => {
        // Only fetch trip details if we have a tripId (web usage)
        // For mobile app with query params, we don't need to fetch
        if (tripId && !useQueryParams) {
            fetchTripDetails();
        }

        // Fetch driver location from backend using existing pattern
        const fetchDriverLocation = async () => {
            try {
                // Only fetch if we have trip data with driver_id
                if (!tripData.length || !tripData[0].driver_id) {
                    console.log("⏳ Waiting for trip data to get driver_id...");
                    return;
                }

                console.log("🔍 Fetching driver location from backend...");
                // Use the same endpoint that LocationTracker updates
                const response = await fetch(`${API_URL}/driver/${tripData[0].driver_id}`);
                const driverArray = await response.json();
                
                if (Array.isArray(driverArray) && driverArray.length > 0) {
                    const driver = driverArray[0];
                    console.log("🚗 Driver location received from backend:", driver);
                    
                    // Use the same field names that LocationTracker updates
                    const lat = parseFloat(driver.lat_cordinates);
                    const lng = parseFloat(driver.long_cordinates);
                    
                    if (!isNaN(lat) && !isNaN(lng)) {
                        setDriverLocation({ latitude: lat, longitude: lng });
                        console.log("✅ Driver location set from backend:", {
                            lat: lat,
                            lng: lng,
                            driver_id: tripData[0].driver_id,
                            source: "LocationTracker real-time updates"
                        });
                    } else {
                        console.error("❌ Invalid coordinate values:", driver);
                    }
                } else {
                    console.error("❌ No driver data found or unexpected format");
                }
            } catch (error) {
                console.error("❌ Error fetching driver location from backend:", error);
                console.log("⏳ Waiting for backend driver location...");
            }
        };

        // Initial fetch
        fetchDriverLocation();

        // Set up polling for real-time updates (every 15 seconds to match LocationTracker)
        const locationInterval = setInterval(fetchDriverLocation, 15000);

        return () => clearInterval(locationInterval);
    }, [tripId, useQueryParams, tripData]);

    useEffect(() => {
        console.log("📍 Driver location state updated:", driverLocation);
        if (driverLocation) {
            console.log("✅ Driver marker should be visible at:", {
                lat: driverLocation.latitude,
                lng: driverLocation.longitude
            });
        } else {
            console.log("❌ No driver location - marker will not show");
        }
    }, [driverLocation]);

    useEffect(() => {
        const fetchDirections = async () => {
            if (mapLoaded && window.google && window.google.maps) {
                console.log("🗺️ Google Maps loaded, fetching directions...");
                const directionsService = new window.google.maps.DirectionsService();
                try {
                    let origin, destination;
                    
                    if (useQueryParams) {
                        // Use query parameters from mobile app
                        console.log("📍 Using query parameters for directions");
                        origin = new window.google.maps.LatLng(
                            parseFloat(startLat),
                            parseFloat(startLng)
                        );
                        destination = new window.google.maps.LatLng(
                            parseFloat(endLat),
                            parseFloat(endLng)
                        );
                    } else if (tripData.length) {
                        // Use trip data from API
                        console.log("📍 Using trip data for directions");
                        origin = new window.google.maps.LatLng(
                            tripData[0].origin_location_lat,
                            tripData[0].origin_location_long
                        );
                        destination = new window.google.maps.LatLng(
                            tripData[0].destination_lat,
                            tripData[0].destination_long
                        );
                    } else {
                        console.log("❌ No data available for directions");
                        return;
                    }

                    console.log("🛣️ Requesting directions from:", origin, "to:", destination);
                    const result = await directionsService.route({
                        origin,
                        destination,
                        travelMode: window.google.maps.TravelMode.DRIVING,
                    });
                    console.log("✅ Directions received:", result);
                    setDirections(result);
                    setTripInfo({
                        distance: result.routes[0].legs[0].distance.text,
                        duration: result.routes[0].legs[0].duration.text,
                        steps: result.routes[0].legs[0].steps,
                    });
                } catch (error) {
                    console.error("❌ Error fetching directions: ", error);
                }
            } else {
                console.log("⏳ Waiting for Google Maps to load...", {
                    mapLoaded: mapLoaded,
                    hasGoogle: !!window.google,
                    hasGoogleMaps: !!(window.google && window.google.maps)
                });
            }
        };

        if (useQueryParams || tripData.length) {
            fetchDirections();
        }
    }, [mapLoaded, tripData, useQueryParams, startLat, startLng, endLat, endLng]);

    useEffect(() => {
        if (driverLocation && tripInfo && window.google && window.google.maps && window.google.maps.geometry) {
            console.log("🔊 Checking driver proximity to route steps...");
            const { steps } = tripInfo;
            steps.forEach((step) => {
                const stepLocation = {
                    lat: step.end_location.lat(),
                    lng: step.end_location.lng(),
                };

                const distanceToStep = window.google.maps.geometry.spherical.computeDistanceBetween(
                    new window.google.maps.LatLng(driverLocation.latitude, driverLocation.longitude),
                    new window.google.maps.LatLng(stepLocation.lat, stepLocation.lng)
                );

                if (distanceToStep < 100) {
                    console.log("📢 Speaking direction:", step.instructions);
                    speakDirection(step.instructions);
                }
            });
        } else {
            console.log("⏳ Waiting for Google Maps geometry library...", {
                hasDriverLocation: !!driverLocation,
                hasTripInfo: !!tripInfo,
                hasGoogle: !!window.google,
                hasGoogleMaps: !!(window.google && window.google.maps),
                hasGeometry: !!(window.google && window.google.maps && window.google.maps.geometry)
            });
        }
    }, [driverLocation, tripInfo]);

    const toggleInfoPanel = useCallback(() => {
        setIsInfoPanelOpen((prev) => !prev);
    }, []);

    return (
        <div className="relative h-screen w-full">
            <LoadScript googleMapsApiKey={getGoogleMapsApiKey()}>
                <GoogleMap
                    mapContainerStyle={{ height: "100%", width: "100%" }}
                    center={center}
                    zoom={15}
                    options={{
                        disableDefaultUI: true,
                        zoomControl: true,
                        styles: [
                            {
                                featureType: "all",
                                elementType: "geometry",
                                stylers: [{ saturation: -80 }],
                            },
                        ],
                    }}
                    onLoad={() => setMapLoaded(true)}
                >
                    {(tripData.length > 0 || useQueryParams) && mapLoaded && window.google && window.google.maps && (
                        <>
                            {/* Origin Marker - Green circle with "O" label */}
                            <Marker
                                position={{
                                    lat: useQueryParams ? parseFloat(startLat) : tripData[0].origin_location_lat,
                                    lng: useQueryParams ? parseFloat(startLng) : tripData[0].origin_location_long,
                                }}
                                title="Origin"
                                label="O"
                                icon={{
                                    path: window.google.maps.SymbolPath.CIRCLE,
                                    scale: 18,
                                    fillColor: "#4CAF50", // Material Design Green
                                    fillOpacity: 1,
                                    strokeColor: "#9B59B6", // Purple outline
                                    strokeWeight: 3,
                                    labelOrigin: new window.google.maps.Point(0, 0)
                                }}
                            />
                            {/* Destination Marker - Red circle with "D" label */}
                            <Marker
                                position={{
                                    lat: useQueryParams ? parseFloat(endLat) : tripData[0].destination_lat,
                                    lng: useQueryParams ? parseFloat(endLng) : tripData[0].destination_long,
                                }}
                                title="Destination"
                                label="D"
                                icon={{
                                    path: window.google.maps.SymbolPath.CIRCLE,
                                    scale: 18,
                                    fillColor: "#E74C3C", // Material Design Red
                                    fillOpacity: 1,
                                    strokeColor: "#9B59B6", // Purple outline
                                    strokeWeight: 3,
                                    labelOrigin: new window.google.maps.Point(0, 0)
                                }}
                            />
                            {driverLocation && (
                                <>
                                    {console.log("✅ Driver marker rendering - creating glow and main marker")}
                                    {/* Glow effect - larger, semi-transparent purple circle */}
                                    <Marker 
                                        position={{
                                            lat: driverLocation.latitude,
                                            lng: driverLocation.longitude,
                                        }}
                                        title="Driver Current Location" 
                                        icon={{
                                            path: window.google.maps.SymbolPath.CIRCLE,
                                            scale: 24,
                                            fillColor: "#9B59B6", // Vibrant purple
                                            fillOpacity: 0.3,
                                            strokeColor: "transparent",
                                            strokeWeight: 0,
                                        }}
                                    />
                                    {/* Main marker - smaller, solid purple circle with car emoji */}
                                    <Marker 
                                        position={{
                                            lat: driverLocation.latitude,
                                            lng: driverLocation.longitude,
                                        }}
                                        title="Driver Current Location" 
                                        label="🚗"
                                        icon={{
                                            path: window.google.maps.SymbolPath.CIRCLE,
                                            scale: 16,
                                            fillColor: "#9B59B6", // Vibrant purple
                                            fillOpacity: 1,
                                            strokeColor: "#FFFFFF",
                                            strokeWeight: 3,
                                            labelOrigin: new window.google.maps.Point(0, 0)
                                        }}
                                    />
                                </>
                            )}
                            {!driverLocation && console.log("❌ Driver marker NOT rendering - no driver location available")}
                            {directions && (
                                <DirectionsRenderer
                                    directions={directions}
                                    options={{
                                        suppressMarkers: true,
                                        polylineOptions: {
                                            strokeColor: "#9B59B6", // Purple to match driver marker
                                            strokeWeight: 5,
                                            strokeOpacity: 0.8,
                                        },
                                    }}
                                />
                            )}
                        </>
                    )}
                </GoogleMap>
            </LoadScript>

            {/* Toggle Button for mobile */}
            <button
                onClick={toggleInfoPanel}
                className="fixed bottom-4 right-4 md:hidden bg-[#ffc000] p-4 rounded-full shadow-lg hover:bg-[#e6ac00] transition-colors"
            >
                <MapPin className="text-white" />
            </button>
        </div>
    );
};

export default Director;